import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  QarepaBaseSubsDirective,
  QarepaProgressBarService,
} from '@qarepa/common';
import {
  addYears,
  format,
  getYear,
  isValid,
  parseISO,
  subYears,
} from 'date-fns';

@Component({
  selector: 'qaroni-year-datepicker',
  templateUrl: './year-datepicker.component.html',
  styleUrls: ['./year-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class YearDatepickerComponent
  extends QarepaBaseSubsDirective
  implements OnInit, OnChanges
{
  readonly title = input<string>('');
  readonly year = input<string>('');
  readonly useQueryParams = input<boolean>(true);
  readonly yearParamName = input<string>('year');
  readonly disableFutureYears = input<boolean>(false);
  readonly borderLess = input<boolean>(true);

  readonly dateYear = output<Date>();

  private route = inject(ActivatedRoute);
  private progressBar = inject(QarepaProgressBarService);

  public isLoading$ = this.progressBar.getProgressBar$();
  public yearControl = new UntypedFormControl();
  public currentDate: Date | null = null;
  public minDate = subYears(new Date(), 100);
  public maxDate = this.disableFutureYears()
    ? new Date()
    : addYears(new Date(), 5);

  private queryParamMap$ = this.route.queryParamMap;

  ngOnInit(): void {
    if (this.useQueryParams()) {
      this.subs.add(this.queryParamMap$.subscribe(this.getQueryParamMap));
    } else {
      this.setCurrentYear();
    }
  }

  ngOnChanges(): void {
    if (!this.useQueryParams()) {
      const date: Date = parseISO(this.year());
      this.setValueYear(date);
    }
  }

  get isDisableFutureYears(): boolean {
    return (
      this.disableFutureYears() && parseInt(this.year()) >= getYear(new Date())
    );
  }

  get ngConditionalsClasses() {
    return {
      invisible: !this.year(),
      border: !this.borderLess(),
    };
  }

  public setYear(normalizedYear: Date, datepicker: MatDatepicker<Date>) {
    this.yearControl.setValue(normalizedYear);
    this.dateYear.emit(this.yearControl.value);
    datepicker.close();
  }

  private setCurrentYear() {
    const today = new Date();
    this.yearControl.setValue(format(today, 'yyyy-MM-dd'));
  }

  private setValueYear(date: Date) {
    const currentDate = isValid(date) ? date : new Date();
    this.yearControl.setValue(format(currentDate, 'yyyy-MM-dd'));
  }

  public addYear() {
    this.yearControl.setValue(addYears(parseISO(this.yearControl.value), 1));
    this.dateYear.emit(this.yearControl.value);
  }

  public subtractYear() {
    this.yearControl.setValue(addYears(parseISO(this.yearControl.value), -1));
    this.dateYear.emit(this.yearControl.value);
  }

  private getQueryParamMap = (queryParamMap: ParamMap): void => {
    const yearParamName = this.yearParamName();
    if (queryParamMap.has(yearParamName)) {
      const date: Date = parseISO(queryParamMap.get(yearParamName));
      this.currentDate = isValid(date) ? date : new Date();
      this.yearControl.setValue(format(this.currentDate, 'yyyy-MM-dd'));
    } else {
      this.setCurrentYear();
    }
  };
}
